import NodeCache from 'node-cache'
import { reportError } from '@mc/next-newrelic'
import { logger } from '@mc/pino-logger'

const CACHE_KEY = 'jwks'
const TTL_SECONDS = 60 * 60 * 24
const RETRY_BACKOFF_MS = [100, 300]

const cache = new NodeCache({ useClones: false })

export interface JsonWebKeySet {
  keys: unknown[]
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const isRetryableHttpStatus = (status: number) => status >= 500

const fetchJwksWithRetry = async (url: string): Promise<JsonWebKeySet> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= RETRY_BACKOFF_MS.length; attempt++) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(RETRY_BACKOFF_MS[attempt - 1])
      logger.warn({
        msg: `Retrying JWKS fetch (attempt ${attempt + 1})`,
        url,
      })
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url)
      if (response.ok) {
        // eslint-disable-next-line no-await-in-loop
        return (await response.json()) as JsonWebKeySet
      }
      const error = new Error(`JWKS fetch failed: ${response.status}`)
      if (!isRetryableHttpStatus(response.status)) throw error
      lastError = error
    } catch (error) {
      if (
        error instanceof Error &&
        /^JWKS fetch failed: [^5]/.test(error.message)
      ) {
        throw error
      }
      lastError = error
    }
  }
  throw lastError
}

export const getJwks = async (): Promise<JsonWebKeySet> => {
  const url = process.env.CORE_AUTH_JWKS_URI
  if (!url) {
    throw new Error('CORE_AUTH_JWKS_URI is not configured')
  }

  const cached = cache.get<JsonWebKeySet>(CACHE_KEY)
  if (cached) return cached

  try {
    const jwks = await fetchJwksWithRetry(url)
    cache.set(CACHE_KEY, jwks, TTL_SECONDS)
    return jwks
  } catch (error) {
    logger.error({ msg: `Failed to fetch JWKS from ${url}`, error })
    reportError(error, { location: 'getJwks' })
    throw error
  }
}
